/**
 * Plugin Sidebar Registry
 *
 * Frontend registry for plugin-provided sidebar sections and items.
 * Works in conjunction with backend PluginSidebarRegistryService.
 * Part of Phase 0: Core Preparation for Modularization.
 */

import { create } from 'zustand';

/**
 * Sidebar section definition
 */
export interface SidebarSection {
  /** Unique section ID */
  id: string;
  /** Plugin that owns this section */
  pluginId: string;
  /** Section title */
  title: string;
  /** Section icon */
  icon?: string;
  /** Order in sidebar */
  order: number;
  /** Whether section is collapsible */
  collapsible: boolean;
  /** Default collapsed state */
  defaultCollapsed: boolean;
  /** Items in this section */
  items: SidebarItem[];
}

/**
 * Sidebar item definition
 */
export interface SidebarItem {
  /** Unique item ID */
  id: string;
  /** Parent section ID */
  sectionId: string;
  /** Plugin that owns this item */
  pluginId: string;
  /** Item label */
  label: string;
  /** Item icon */
  icon?: string;
  /** Route path or URL */
  path: string;
  /** Whether path is external */
  isExternal?: boolean;
  /** Order within section */
  order: number;
  /** Badge text */
  badge?: string | number;
  /** Badge variant */
  badgeVariant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  /** Whether item is active */
  isActive?: boolean;
  /** Tooltip text */
  tooltip?: string;
  /** Sub-items for nested navigation */
  children?: SidebarItem[];
}

/**
 * Sidebar registry state
 */
interface SidebarRegistryState {
  /** Map of section ID to definition */
  sections: Map<string, SidebarSection>;
  /** Map of item ID to definition */
  items: Map<string, SidebarItem>;
  /** Collapsed state per section */
  collapsedState: Map<string, boolean>;
  
  // Actions
  registerSection: (section: Omit<SidebarSection, 'items'>) => void;
  registerItem: (item: SidebarItem) => void;
  unregisterSection: (sectionId: string) => void;
  unregisterItem: (itemId: string) => void;
  unregisterPluginItems: (pluginId: string) => void;
  toggleSection: (sectionId: string) => void;
  setCollapsed: (sectionId: string, collapsed: boolean) => void;
  updateItemBadge: (itemId: string, badge: string | number | undefined) => void;
  getSortedSections: () => SidebarSection[];
  getSection: (sectionId: string) => SidebarSection | undefined;
  syncFromBackend: (sections: SidebarSection[]) => void;
}

/**
 * Sidebar registry store
 */
export const useSidebarRegistry = create<SidebarRegistryState>((set, get) => ({
  sections: new Map(),
  items: new Map(),
  collapsedState: new Map(),

  registerSection: (section: Omit<SidebarSection, 'items'>) => {
    set((state) => {
      const newSections = new Map(state.sections);
      const existing = newSections.get(section.id);
      
      newSections.set(section.id, {
        ...section,
        items: existing?.items ?? [],
      });
      
      // Initialize collapsed state
      const newCollapsed = new Map(state.collapsedState);
      if (!newCollapsed.has(section.id)) {
        newCollapsed.set(section.id, section.defaultCollapsed);
      }
      
      return { sections: newSections, collapsedState: newCollapsed };
    });
  },

  registerItem: (item: SidebarItem) => {
    set((state) => {
      const newItems = new Map(state.items);
      const newSections = new Map(state.sections);
      
      newItems.set(item.id, item);
      
      // Add to section
      const section = newSections.get(item.sectionId);
      if (section) {
        const existingIndex = section.items.findIndex(i => i.id === item.id);
        if (existingIndex >= 0) {
          section.items[existingIndex] = item;
        } else {
          section.items.push(item);
        }
        section.items.sort((a, b) => a.order - b.order);
        newSections.set(item.sectionId, { ...section });
      }
      
      return { items: newItems, sections: newSections };
    });
  },

  unregisterSection: (sectionId: string) => {
    set((state) => {
      const section = state.sections.get(sectionId);
      if (!section) return state;
      
      const newSections = new Map(state.sections);
      const newItems = new Map(state.items);
      const newCollapsed = new Map(state.collapsedState);
      
      // Remove all items in section
      for (const item of section.items) {
        newItems.delete(item.id);
      }
      
      newSections.delete(sectionId);
      newCollapsed.delete(sectionId);
      
      return { sections: newSections, items: newItems, collapsedState: newCollapsed };
    });
  },

  unregisterItem: (itemId: string) => {
    set((state) => {
      const item = state.items.get(itemId);
      if (!item) return state;
      
      const newItems = new Map(state.items);
      const newSections = new Map(state.sections);
      
      newItems.delete(itemId);
      
      // Remove from section
      const section = newSections.get(item.sectionId);
      if (section) {
        section.items = section.items.filter(i => i.id !== itemId);
        newSections.set(item.sectionId, { ...section });
      }
      
      return { items: newItems, sections: newSections };
    });
  },

  unregisterPluginItems: (pluginId: string) => {
    set((state) => {
      const newSections = new Map(state.sections);
      const newItems = new Map(state.items);
      const newCollapsed = new Map(state.collapsedState);
      
      // Remove sections owned by plugin
      for (const [id, section] of newSections) {
        if (section.pluginId === pluginId) {
          for (const item of section.items) {
            newItems.delete(item.id);
          }
          newSections.delete(id);
          newCollapsed.delete(id);
        }
      }
      
      // Remove items owned by plugin from other sections
      for (const [id, item] of newItems) {
        if (item.pluginId === pluginId) {
          newItems.delete(id);
          
          const section = newSections.get(item.sectionId);
          if (section) {
            section.items = section.items.filter(i => i.id !== id);
            newSections.set(item.sectionId, { ...section });
          }
        }
      }
      
      return { sections: newSections, items: newItems, collapsedState: newCollapsed };
    });
  },

  toggleSection: (sectionId: string) => {
    set((state) => {
      const newCollapsed = new Map(state.collapsedState);
      const current = newCollapsed.get(sectionId) ?? false;
      newCollapsed.set(sectionId, !current);
      return { collapsedState: newCollapsed };
    });
  },

  setCollapsed: (sectionId: string, collapsed: boolean) => {
    set((state) => {
      const newCollapsed = new Map(state.collapsedState);
      newCollapsed.set(sectionId, collapsed);
      return { collapsedState: newCollapsed };
    });
  },

  updateItemBadge: (itemId: string, badge: string | number | undefined) => {
    set((state) => {
      const item = state.items.get(itemId);
      if (!item) return state;
      
      const newItems = new Map(state.items);
      const newSections = new Map(state.sections);
      
      const updatedItem = { ...item, badge };
      newItems.set(itemId, updatedItem);
      
      // Update in section
      const section = newSections.get(item.sectionId);
      if (section) {
        const index = section.items.findIndex(i => i.id === itemId);
        if (index >= 0) {
          section.items[index] = updatedItem;
          newSections.set(item.sectionId, { ...section });
        }
      }
      
      return { items: newItems, sections: newSections };
    });
  },

  getSortedSections: () => {
    const sections = Array.from(get().sections.values());
    return sections.sort((a, b) => a.order - b.order);
  },

  getSection: (sectionId: string) => {
    return get().sections.get(sectionId);
  },

  syncFromBackend: (sections: SidebarSection[]) => {
    set((state) => {
      const newSections = new Map<string, SidebarSection>();
      const newItems = new Map<string, SidebarItem>();
      const newCollapsed = new Map(state.collapsedState);
      
      for (const section of sections) {
        newSections.set(section.id, section);
        
        if (!newCollapsed.has(section.id)) {
          newCollapsed.set(section.id, section.defaultCollapsed);
        }
        
        for (const item of section.items) {
          newItems.set(item.id, item);
        }
      }
      
      return { sections: newSections, items: newItems, collapsedState: newCollapsed };
    });
  },
}));

/**
 * Hook to check if section is collapsed
 */
export function useSectionCollapsed(sectionId: string): boolean {
  return useSidebarRegistry((state) => state.collapsedState.get(sectionId) ?? false);
}
